import { Button, Dialog, Popover, useKumoToastManager } from '@cloudflare/kumo';
import { Archive, PencilSimple, Plus, X } from '@phosphor-icons/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import type { paths } from '../../generated/api.ts';
import { writable } from '../../shared/content.ts';
import { submissionActionFields } from '../../shared/form-actions.ts';
import { api, isActivationRequired, queryClient, sitesQuery, unwrap } from '../api.ts';
import { ErrorNotice, Loading, previewDialogClassName } from '../common.tsx';
import { SiteActivation } from './site-activation.tsx';

type Group =
  paths['/api/unpublished-changes']['get']['responses'][200]['content']['application/json'][number];
type Change = Group['changes'][number];

async function readChangeObject(siteId: string, change: Change, version: 'Draft' | 'Published') {
  switch (change.kind) {
    case 'Settings':
      return unwrap(
        await api.GET('/api/sites/{siteId}/settings', { params: { path: { siteId }, query: { version } } }),
      );
    case 'Page':
    case 'Post':
      return unwrap(
        await api.GET('/api/pages/{pageId}', {
          params: { path: { pageId: change.objectId }, query: { version } },
        }),
      );
    case 'Form':
      return unwrap(
        await api.GET('/api/forms/{formId}', {
          params: { path: { formId: change.objectId }, query: { version } },
        }),
      );
    case 'Asset':
      return unwrap(
        await api.GET('/api/assets/{assetId}', {
          params: { path: { assetId: change.objectId }, query: { version } },
        }),
      );
    case 'FormSubmissionAction':
      return unwrap(
        await api.GET('/api/form-submission-actions/{id}', {
          params: { path: { id: change.objectId }, query: { version } },
        }),
      );
  }
}

function ChangeDetails({ siteId, change }: { siteId: string; change: Change }) {
  const details = useQuery({
    queryKey: [
      'site',
      siteId,
      'unpublished-change',
      change.kind,
      change.objectId,
      change.draftVersionId,
      change.publishedVersionId,
    ],
    queryFn: async () => {
      const [published, draft] = await Promise.all([
        change.publishedVersionId ? readChangeObject(siteId, change, 'Published') : null,
        change.change === 'Archived' ? null : readChangeObject(siteId, change, 'Draft'),
      ]);
      return { published, draft };
    },
  });
  if (details.isPending) return <Loading label="Loading changes…" />;
  if (details.isError)
    return (
      <div className="p-6">
        <ErrorNotice error={details.error} />
        <Button disabled={details.isFetching} onClick={() => void details.refetch()}>
          Retry
        </Button>
      </div>
    );
  const { published, draft } = details.data;
  return (
    <div className="grid grid-cols-2 flex-1 min-h-0 overflow-auto [&_section]:p-6 [&_section]:min-w-0 [&_section_+_section]:border-l [&_section_+_section]:border-l-kumo-line [&_h3]:mx-0 [&_h3]:mt-0 [&_h3]:mb-4 [&_h3]:text-[13px] [&_h3]:font-semibold [&_h3]:text-kumo-subtle [&_pre]:text-[12px] [&_pre]:leading-[1.7] [&_pre]:whitespace-pre-wrap [&_pre]:wrap-anywhere max-[768px]:grid-cols-1 max-[768px]:[&_section_+_section]:border-t max-[768px]:[&_section_+_section]:border-l-0 max-[768px]:[&_section_+_section]:border-t-kumo-line max-[768px]:[&_section_+_section]:border-l-current">
      <section>
        <h3 className="text-[16px] font-semibold">Published</h3>
        <pre>
          {published
            ? JSON.stringify(
                published.kind === 'FormSubmissionAction'
                  ? submissionActionFields(published)
                  : writable(published),
                null,
                2,
              )
            : 'Not published'}
        </pre>
      </section>
      <section>
        <h3 className="text-[16px] font-semibold">Draft</h3>
        <pre>
          {draft
            ? JSON.stringify(
                draft.kind === 'FormSubmissionAction' ? submissionActionFields(draft) : writable(draft),
                null,
                2,
              )
            : 'Archived'}
        </pre>
      </section>
    </div>
  );
}

export function UnpublishedChanges() {
  const sites = useQuery(sitesQuery);
  const [open, setOpen] = useState(() => new URLSearchParams(location.search).get('changes') === 'true');
  const [selection, setSelection] = useState<{ siteId: string; kind: Change['kind']; objectId: string }>();
  const [activationSite, setActivationSite] = useState<{ id: string; accountId: string }>();
  const toast = useKumoToastManager();
  const changes = useQuery({
    queryKey: ['sites', 'unpublished-changes'],
    queryFn: async () => unwrap(await api.GET('/api/unpublished-changes')),
    enabled: open || Boolean(selection),
  });
  const publish = useMutation({
    mutationFn: async (siteId: string) =>
      unwrap(await api.POST('/api/sites/{siteId}/publications', { params: { path: { siteId } }, body: {} })),
    onSuccess: async () => {
      setActivationSite(undefined);
      toast.add({ title: 'Published — your changes are live.', variant: 'success' });
      await queryClient.invalidateQueries();
    },
    onError: (error, siteId) => {
      const site = sites.data?.find((entry) => entry.id === siteId);
      if (isActivationRequired(error) && site) {
        setActivationSite({ id: siteId, accountId: site.ownerUserId });
        setOpen(false);
      }
    },
  });
  const siteCount = sites.data?.filter((site) => site.unpublishedChangeCount > 0).length;
  const label = siteCount
    ? `Unpublished changes · ${siteCount} ${siteCount === 1 ? 'site' : 'sites'}`
    : 'Unpublished changes';
  const selectedGroup = changes.data?.find((group) => group.site.id === selection?.siteId);
  const selected = selection
    ? selectedGroup?.changes.find(
        (change) => change.kind === selection.kind && change.objectId === selection.objectId,
      )
    : undefined;
  if (selection && changes.isSuccess && !selected) setSelection(undefined);
  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <Popover.Trigger render={<Button type="button" disabled={siteCount === 0} aria-label={label} />}>
          <span className="max-[760px]:hidden">{label}</span>
          <span className="min-[761px]:hidden">Changes{siteCount ? ` · ${siteCount}` : ''}</span>
        </Popover.Trigger>
        <Popover.Content align="end" className="p-0 w-110 max-w-[calc(100vw_-_24px)] z-50">
          <Popover.Title className="px-4 py-3 border-b border-kumo-line text-sm font-semibold">
            Unpublished changes
          </Popover.Title>
          <div className="max-h-[min(480px,_65dvh)] overflow-y-auto">
            <div className="px-4 [&:empty]:hidden">
              <ErrorNotice error={changes.error ?? sites.error} />
            </div>
            {changes.isPending ? (
              <Loading />
            ) : changes.data?.length ? (
              changes.data.map((group) => (
                <section
                  key={group.site.id}
                  aria-label={`Changes for ${group.site.name}`}
                  className="p-4 [&_+_section]:border-t [&_+_section]:border-kumo-line"
                >
                  <div className="flex items-center justify-between gap-4 mb-3">
                    <div className="min-w-0">
                      <h2 className="text-sm font-semibold wrap-anywhere">{group.site.name}</h2>
                      <p className="text-xs text-kumo-subtle">
                        {group.changes.length} {group.changes.length === 1 ? 'change' : 'changes'}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      disabled={publish.isPending}
                      onClick={() => publish.mutate(group.site.id)}
                    >
                      {publish.isPending && publish.variables === group.site.id ? 'Publishing…' : 'Publish'}
                    </Button>
                  </div>
                  <ErrorNotice
                    error={
                      publish.variables === group.site.id && !isActivationRequired(publish.error)
                        ? publish.error
                        : undefined
                    }
                  />
                  <ul aria-label="Unpublished changes" className="m-0 p-0 list-none grid gap-2">
                    {group.changes.map((change) => (
                      <li key={`${change.kind}:${change.objectId}`} className="flex items-center gap-3">
                        <span
                          className="flex shrink-0 text-kumo-subtle"
                          role="img"
                          aria-label={change.change}
                          title={change.change}
                        >
                          {change.change === 'Added' ? (
                            <Plus size={16} />
                          ) : change.change === 'Archived' ? (
                            <Archive size={16} />
                          ) : (
                            <PencilSimple size={16} />
                          )}
                        </span>
                        <span className="text-sm flex-1 min-w-0 truncate" title={change.label}>
                          {change.label}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelection({
                              siteId: group.site.id,
                              kind: change.kind,
                              objectId: change.objectId,
                            });
                            setOpen(false);
                          }}
                          aria-label={`View changes for ${change.label}`}
                        >
                          View
                        </Button>
                      </li>
                    ))}
                  </ul>
                </section>
              ))
            ) : !changes.error ? (
              <p className="p-4 text-sm text-kumo-subtle">No unpublished changes.</p>
            ) : null}
          </div>
        </Popover.Content>
      </Popover>
      {activationSite && (
        <SiteActivation
          siteId={activationSite.id}
          accountId={activationSite.accountId}
          onActivated={() => publish.mutateAsync(activationSite.id)}
          onClose={() => setActivationSite(undefined)}
        />
      )}
      <Dialog.Root
        open={Boolean(selected)}
        onOpenChange={(open) => {
          if (!open) setSelection(undefined);
        }}
      >
        <Dialog className={`${previewDialogClassName} flex flex-col`} aria-describedby={undefined}>
          {selected && selectedGroup && (
            <>
              <div className="px-6 py-5 border-b border-b-kumo-line flex justify-between items-center gap-4 [&_h2]:m-0 [&_h2]:text-[18px] [&_h2]:font-semibold [&_h2]:tracking-[-0.35px] [&_h2]:wrap-anywhere">
                <div>
                  <Dialog.Title>{selected.label}</Dialog.Title>
                  <span className="block text-[11px] text-kumo-subtle mt-[3px]">
                    {selectedGroup.site.name} · {selected.change}
                  </span>
                </div>
                <Button aria-label="Close change details" onClick={() => setSelection(undefined)}>
                  <X size={20} />
                </Button>
              </div>
              <ChangeDetails siteId={selectedGroup.site.id} change={selected} />
            </>
          )}
        </Dialog>
      </Dialog.Root>
    </>
  );
}
